import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { cx } from './cx.js';

export interface MenuItem {
  key: string;
  label: string;
  icon?: ReactNode;
  onSelect: () => void;
}

/**
 * Bouton qui ouvre un petit menu d'actions (modèle « menu button » de l'ARIA APG).
 * Clavier : Entrée, Espace ou Flèche bas ouvrent et placent le focus sur la première entrée ; flèches,
 * Début, Fin pour se déplacer ; Échap referme et rend le focus au bouton ; Tab referme. Un appui hors
 * du menu le referme. `placement` : au-dessus (bas de barre latérale) ou en dessous (en-tête).
 */
export function MenuButton({
  label,
  items,
  children,
  header,
  placement = 'below-end',
  className,
  menuClassName,
}: {
  /** Nom accessible du bouton (ex. « Menu du compte, Camille Laurent »). */
  label: string;
  items: MenuItem[];
  /** Contenu visible du bouton (avatar, nom…). */
  children: ReactNode;
  /** En-tête non interactif du menu (nom, e-mail). */
  header?: ReactNode;
  placement?: 'above-start' | 'below-end';
  className?: string;
  menuClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const entries = () => Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
  const focusAt = (i: number) => {
    const list = entries();
    if (list.length === 0) return;
    list[(i + list.length) % list.length]?.focus();
  };
  const close = useCallback((restoreFocus: boolean) => {
    setOpen(false);
    if (restoreFocus) buttonRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    const onPointer = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close(false);
    };
    document.addEventListener('pointerdown', onPointer);
    return () => document.removeEventListener('pointerdown', onPointer);
  }, [open, close]);

  const onMenuKey = (e: React.KeyboardEvent) => {
    const list = entries();
    const i = list.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'ArrowDown') focusAt(i + 1);
    else if (e.key === 'ArrowUp') focusAt(i - 1);
    else if (e.key === 'Home') focusAt(0);
    else if (e.key === 'End') focusAt(list.length - 1);
    else if (e.key === 'Escape') close(true);
    else if (e.key === 'Tab') return close(false);
    else return;
    e.preventDefault();
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            setOpen(true);
          }
        }}
        className={className}
      >
        {children}
      </button>
      {open ? (
        <div
          ref={menuRef}
          id={id}
          role="menu"
          aria-label={label}
          onKeyDown={onMenuKey}
          className={cx(
            'absolute z-40 min-w-56 overflow-hidden rounded-xl bg-surface-raised p-1 text-ink shadow-lg ring-1 ring-line',
            'motion-safe:animate-[ui-fade_var(--dur-fast)_var(--ease-out)]',
            placement === 'above-start' ? 'bottom-full left-0 mb-2' : 'top-full right-0 mt-2',
            menuClassName,
          )}
        >
          {header ? <div className="border-b border-line px-3 pt-2 pb-2.5">{header}</div> : null}
          <div className={header ? 'pt-1.5' : undefined}>
            {items.map((item) => (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                tabIndex={-1}
                onClick={() => {
                  close(false);
                  item.onSelect();
                }}
                className="flex min-h-(--ctl-row) w-full items-center gap-2.5 rounded-md px-2.5 text-left text-base font-medium text-ink outline-none hover:bg-sunken focus-visible:bg-sunken focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus"
              >
                {item.icon ? (
                  <span aria-hidden className="shrink-0 text-ink-3">
                    {item.icon}
                  </span>
                ) : null}
                {item.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
